import React, { useState, useEffect, useRef } from 'react';
import UploaderMultiple from './UploaderMultiple';
import MamAssetCard from './MamAssetCard';
import MamFilterBar from './MamFilterBar';
import MamPaginator from './MamPaginator';
import MamObjectForm from './MamObjectForm';
import Modal from '../../Modal/Modal';
import getMamObjectsBySlugName from '../../../api/graphql/queries/getMamObjectsBySlugname';
import getMamObjectsListWithTotal from '../../../api/graphql/queries/getMamObjectsListWithTotal';
import addEditMamObject from '../../../api/graphql/mutations/addEditMamObject';
import deleteMamObject from '../../../api/graphql/mutations/deleteMamObject';
import Filter from '../../Tabs/Filter';
import LoadIcon from '../../LoadIcon';

const PER_PAGE = 100;

const TYPE_MAP = {
  all: undefined,
  images: ['image'],
  other: ['video', 'audio', 'file'],
};

const VISIBILITY_MAP = {
  all: undefined,
  public: true,
  private: false,
};

const initialFilters = {
  typeKey: 'all',
  visibility: 'all',
  uploadedBy: '',
  tag: '',
  sort: 'newest',
  search: '',
};

const MamContainer = () => {
  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [uploaders, setUploaders] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [state, setState] = useState();
  const isMounted = useRef(true);
  const searchDebounce = useRef();
  const requestId = useRef(0);

  const fetchList = (activeFilters, activePage) => {
    setIsLoading(true);
    setHasError(false);
    requestId.current += 1;
    const id = requestId.current;
    getMamObjectsListWithTotal({
      type: TYPE_MAP[activeFilters.typeKey],
      search: activeFilters.search || undefined,
      isPublic: VISIBILITY_MAP[activeFilters.visibility],
      uploadedBy: activeFilters.uploadedBy || undefined,
      tags: activeFilters.tag ? [activeFilters.tag] : undefined,
      sort: activeFilters.sort,
      offset: (activePage - 1) * PER_PAGE,
      limit: PER_PAGE,
    }).then((res) => {
      if (!isMounted.current || id !== requestId.current) return;
      if (!res) {
        setHasError(true);
      } else {
        setItems(res.mamObjects || []);
        setTotal(res.total || 0);
        setUploaders(res.uploaders || []);
        setAllTags(res.allTags || []);
      }
      setIsLoading(false);
    });
  };

  useEffect(() => {
    fetchList(filters, page);
  }, [filters, page]);

  useEffect(() => () => { isMounted.current = false; clearTimeout(searchDebounce.current); }, []);

  const handleFilterChange = (partial) => {
    const isUnchanged = Object.keys(partial).every(key => filters[key] === partial[key]);
    if (isUnchanged) return;
    setPage(1);
    setFilters({ ...filters, ...partial });
  };

  const handleSearch = (val) => {
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => handleFilterChange({ search: val }), 300);
  };

  const handlerSetSelectedObj = (slug) => {
    getMamObjectsBySlugName([slug]).then((res) => {
      setState(res[0]);
      setShowForm(true);
    });
  };

  const inputObjHandler = (e) => {
    const { value, lang, name } = e.target;
    setState({ ...state, [name]: { ...state[name], [lang]: value } });
  };

  const makePublicHandler = bool => setState({ ...state, isPublic: bool });

  const handleTags = (name) => {
    const { tags } = state;
    const index = tags.indexOf(name);
    if (index < 0) tags.push(name); else tags.splice(index, 1);
    setState({ ...state, tags });
  };

  const saveObject = () => {
    addEditMamObject({ ...state }).then(() => {
      setShowForm(false);
      fetchList(filters, page);
    }).catch(err => console.log(err));
  };

  const deleteObject = () => {
    deleteMamObject({ ...state }).then(() => {
      setShowForm(false);
      fetchList(filters, page);
    }).catch(err => console.log(err));
  };

  const handleClick = () => setShowForm(!showForm);

  const handleMoreItems = () => {
    setPage(1);
    fetchList(filters, 1);
  };

  return (
    <>
      {showForm && (
        <Modal title="Edit file" handleClick={handleClick} type="display" positionMiddle size="w-auto">
          <MamObjectForm objState={state} makePublicHandler={makePublicHandler} inputObjHandler={inputObjHandler} saveObject={saveObject} close={handleClick} deleteObject={deleteObject} editor toggleTag={handleTags} />
        </Modal>
      )}
      <Filter updateSearch={handleSearch} placeholder="Search name, filename, description or tags" />
      <MamFilterBar filters={filters} onChange={handleFilterChange} uploaders={uploaders} allTags={allTags} />
      <UploaderMultiple handleMoreItems={handleMoreItems} multiple />
      {hasError && (
        <div className="w-full text-center py-8">
          <p className="font-americaMonoRegular text-sm">Could not load assets.</p>
          <button type="button" className="font-americaMonoBold text-xs px-6 py-2 rounded-full bg-white text-black uppercase" onClick={() => fetchList(filters, page)}>Retry</button>
        </div>
      )}
      {!hasError && !isLoading && items.length === 0 && (
        <div className="w-full text-center py-8">
          <p className="font-americaMonoRegular text-sm">No assets match these filters.</p>
          <button type="button" className="font-americaMonoBold text-xs px-6 py-2 rounded-full bg-white text-black uppercase" onClick={() => { setPage(1); setFilters(initialFilters); }}>Clear filters</button>
        </div>
      )}
      <div className="w-full flex flex-row flex-wrap overflow-y-scroll h-carouselMD justify-center">
        {isLoading
          ? <LoadIcon />
          : items.map(obj => <MamAssetCard key={obj.assetSlugName} obj={obj} onOpen={handlerSetSelectedObj} />)
        }
      </div>
      <MamPaginator total={total} page={page} perPage={PER_PAGE} onPage={setPage} />
    </>
  );
};

export default MamContainer;
